/** admin/users — HOOKS. */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { usersApi } from './api';
import { adminUsersKeys } from './keys';
import type { CreateUserPayload, UpdateUserPayload, UserListItem } from './types';
import { useAuthStore } from '../../../services/authentication/auth-store';
import { authService } from '../../../services/authentication/auth-service';

const invalidate = (qc: QueryClient) => qc.invalidateQueries({ queryKey: adminUsersKeys.all() });

/**
 * Keep the header in step when an admin edits their *own* photo.
 *
 * The admin/moderator header reads the signed-in identity from the auth store,
 * which is only filled at login and refreshed by /auth/me at boot — so changing
 * your own photo here updated the database and the users table but left the
 * header showing the old one until a reload. The student area never had this
 * because its avatar comes from a query it re-fetches.
 */
function syncSignedInUser(updated: UserListItem) {
  const { user } = useAuthStore.getState();
  if (!user || user.id !== updated.id) return;
  // Updates the store *and* the persisted session; setting only the store would
  // leave localStorage stale until the next /auth/me — a whole page load of
  // showing the previous photo.
  authService.persistCurrentUser({ ...user, avatarPhotoUrl: updated.avatarPhotoUrl ?? null });
}

export function useAdminUsers(page: number, limit: number, userType?: string) {
  return useQuery({
    queryKey: adminUsersKeys.list({ page, limit, userType }),
    queryFn: () => usersApi.list(page, limit, userType),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (payload: CreateUserPayload) => usersApi.create(payload), onSuccess: () => invalidate(qc) });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; payload: UpdateUserPayload }) => usersApi.update(v.id, v.payload),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateUserPassword() {
  return useMutation({ mutationFn: (v: { id: string; newPassword: string }) => usersApi.updatePassword(v.id, v.newPassword) });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => usersApi.remove(id), onSuccess: () => invalidate(qc) });
}

export function useUploadUserPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; blob: Blob }) => usersApi.uploadPhoto(v.id, v.blob),
    onSuccess: (updated) => {
      syncSignedInUser(updated);
      return invalidate(qc);
    },
  });
}

export function useRemoveUserPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.removePhoto(id),
    onSuccess: (updated) => {
      syncSignedInUser(updated);
      return invalidate(qc);
    },
  });
}
